/**
 * Client-side image downscale to JPEG (base64, no data: prefix).
 *
 * Adapted from the former tipping-game avatar resizer, with the cap raised to
 * ~1600 px so receipt text stays legible for OCR. Keeps payloads small for mobile
 * uploads. HEIC/HEIF generally cannot decode into <img> outside Safari — callers
 * should fall back to sending the original bytes if this throws.
 */
export async function fileToResizedJpeg(
    file: File,
    maxDim = 1600,
    quality = 0.85,
): Promise<{ base64: string; mime: string }> {
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Could not read the file.'));
        reader.readAsDataURL(file);
    });

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Could not decode this image.'));
        el.src = dataUrl;
    });

    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Image processing is not supported in this browser.');
    ctx.drawImage(img, 0, 0, w, h);
    const out = canvas.toDataURL('image/jpeg', quality);
    return { base64: out.split(',')[1] ?? '', mime: 'image/jpeg' };
}

/** Read any file as base64 (no data: prefix) — fallback when resize can't decode (e.g. HEIC). */
export async function fileToBase64(file: File): Promise<{ base64: string; mime: string }> {
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Could not read the file.'));
        reader.readAsDataURL(file);
    });
    const mime = /^data:([^;]+);/.exec(dataUrl)?.[1] || file.type || 'image/jpeg';
    return { base64: dataUrl.split(',')[1] ?? '', mime };
}
